import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaPaperPlane,
  FaCheckCircle,
  FaDownload,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import './Tickets.css';

const TicketDetails = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminResponse, setAdminResponse] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const chatContainerRef = useRef(null);
  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
    const baseUrl = ENDPOINTS.AUTH.LOGIN.replace('/api/auth/login', '').replace('/auth/login', '');
    return `${baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
  };


  const fetchTicketDetails = async () => {
    try {
      // Actually we need an admin endpoint to get single ticket details because user endpoint expects user ID matches
      // But we can just use the admin all list to find it if there's no specific admin details endpoint.
      // Easiest is to fetch all and find by id:
      const res = await api.get(ENDPOINTS.SUPPORT.ADMIN_ALL);
      const data = Array.isArray(res) ? res : (res.data || []);
      const found = data.find(t => t.id === parseInt(id));
      if (found) {
        setTicket(found);
        setAdminResponse(found.adminResponse || '');
      } else {
        toast.error('Ticket not found');
      }
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await api.get(ENDPOINTS.SUPPORT.MESSAGES(id));
      setMessages(Array.isArray(res) ? res : (res.data || []));
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    fetchTicketDetails();
    fetchMessages();

    // Poll for messages every 20 seconds (optimized from 5s to reduce server load)
    const messageInterval = setInterval(() => {
      fetchMessages();
    }, 20000);

    // Occasional status sync (every 60s)
    const statusInterval = setInterval(() => {
      fetchTicketDetails();
    }, 60000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(statusInterval);
    };
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleDownload = async (url) => {
    const toastId = toast.loading('Preparing download...');
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const filename = url.split('/').pop() || 'attachment';
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.update(toastId, { render: 'Download started!', type: 'success', isLoading: false, autoClose: 2000 });
    } catch (error) {
      console.error('Download failed:', error);
      toast.update(toastId, { render: 'Direct download attempted', type: 'info', isLoading: false, autoClose: 2000 });

      // Fallback: Use direct link with hidden download anchor
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = url.split('/').pop() || '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleResolve = async (e) => {
    e.preventDefault();
    if (!adminResponse.trim()) {
      toast.error('Please enter a response/resolution');
      return;
    }

    setIsResolving(true);
    try {
      await api.post(ENDPOINTS.SUPPORT.RESOLVE(id), {
        response: adminResponse
      });
      toast.success('Ticket resolved successfully');
      fetchTicketDetails();
      // Trigger navbar refresh to clear notification badge immediately
      window.dispatchEvent(new CustomEvent('refreshNotifications'));
    } catch (error) {
      console.error('Failed to resolve ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to resolve ticket');
    } finally {
      setIsResolving(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setIsSending(true);
    try {
      await api.post(ENDPOINTS.SUPPORT.MESSAGES(id), {
        message: newMessage
      });
      setNewMessage('');
      fetchMessages();
      // Trigger navbar refresh to clear notification badge immediately
      window.dispatchEvent(new CustomEvent('refreshNotifications'));
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading ticket details...</p>
      </div>
    );
  }

  if (!ticket) return null;

  const isProvider = ticket.userRole === 'caregiver' || ticket.userRole === 'provider' || (!ticket.userRole && ticket.provider_id);
  const user = isProvider ? ticket.provider : ticket.customer;

  return (
    <div className="ticket-details-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <Link to="/support/tickets" className="back-btn">
            <FaArrowLeft />
          </Link>
          <div>
            <h1>Ticket {ticket.ticketId}</h1>
            <p className="page-subtitle mb-0">Raised on {new Date(ticket.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="header-actions">
          <span className={`badge badge-${ticket.status === 'open' ? 'danger' : ticket.status === 'in-progress' ? 'warning' : 'success'} fs-6 py-2 px-3`}>
            {ticket.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="row mt-4">
        {/* Left Column - Ticket Info & Resolution */}
        <div className="col-lg-8">
          <div className="content-card mb-4 details-card">
            <div className="card-header border-bottom pb-3 mb-3">
              <h4 className="mb-0 d-flex justify-content-between">
                <span>{ticket.subject}</span>
                <span className={`priority-badge priority-${ticket.priority}`}>
                  {ticket.priority.toUpperCase()} PRIORITY
                </span>
              </h4>
            </div>
            <div className="ticket-content p-3 bg-light rounded mb-4">
              <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {ticket.description}
              </p>
            </div>

            {ticket.bookingId && (
              <div className="related-booking mb-4 p-3 border rounded border-primary">
                <div className="d-flex align-items-center gap-2 text-primary fw-bold">
                  <span className="badge bg-primary">Context</span>
                  Related to Booking Reference: #{ticket.bookingId}
                </div>
              </div>
            )}

            {ticket.attachments && Array.isArray(ticket.attachments) && ticket.attachments.length > 0 && (
              <div className="attachments-section mb-5">
                <h5 className="section-title mb-3 d-flex align-items-center gap-2">
                  <FaDownload className="text-muted" style={{ fontSize: '0.9rem' }} />
                  Initial Attachments ({ticket.attachments.length})
                </h5>
                <div className="attachments-grid-premium">
                  {ticket.attachments.map((attach, idx) => {
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(attach);
                    const isPdf = /\.pdf$/i.test(attach);

                    return (
                      <div key={idx} className="attachment-item-premium">
                        <div
                          className="attachment-preview-card"
                          onClick={() => isImage ? setPreviewImage(attach) : window.open(attach, '_blank')}
                        >
                          {isImage ? (
                            <div className="img-thumb-wrapper">
                              <img src={attach} alt={`Attachment ${idx + 1}`} />
                              <div className="thumb-hover-overlay">
                                <span className="view-text">Click to View</span>
                              </div>
                            </div>
                          ) : (
                            <div className="file-thumb-wrapper">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                              </svg>
                              <span className="file-type-label">{isPdf ? 'PDF' : 'FILE'}</span>
                            </div>
                          )}
                        </div>
                        <div className="attachment-footer">
                          <span className="attachment-name">Attachment {idx + 1}</span>
                          <button
                            className="btn-action-minimal"
                            onClick={(e) => { e.stopPropagation(); handleDownload(attach); }}
                            title="Download"
                          >
                            <FaDownload />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <hr />

            <div className="chat-section mt-4 mb-5">
              <h5 className="mb-3">Conversation</h5>
              <div 
                className="chat-container bg-white p-3 border rounded shadow-sm mb-3" 
                style={{ height: '400px', overflowY: 'auto' }}
                ref={chatContainerRef}
              >
                {messages.length === 0 ? (
                  <p className="text-muted text-center my-4">No messages yet. Start the conversation!</p>
                ) : (
                  messages.map(msg => {
                    const isAdmin = msg.senderRole === 'admin';
                    return (
                      <div key={msg.id} className={`d-flex mb-3 ${isAdmin ? 'justify-content-end' : 'justify-content-start'}`}>
                        <div className={`chat-bubble p-3 rounded-3 max-w-75 ${isAdmin ? 'bg-primary text-white' : 'bg-light text-dark'}`} style={{ maxWidth: '75%' }}>
                          <div className="d-flex justify-content-between align-items-center mb-1 gap-3">
                            <small className={isAdmin ? 'fw-bold text-white' : 'fw-bold text-primary'}>
                              {isAdmin ? 'You (Admin)' : (msg.senderRole === 'provider' || msg.senderRole === 'caregiver' ? 'Provider' : 'Customer')}
                            </small>
                            <small className="opacity-75" style={{ fontSize: '0.75rem' }}>
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </small>
                          </div>
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{msg.message}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                <form onSubmit={handleSendMessage} className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type a message to the user..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={isSending}
                  />
                  <button type="submit" className="btn btn-primary px-4 d-flex align-items-center gap-2" disabled={isSending || !newMessage.trim()}>
                    {isSending ? 'Sending...' : <><FaPaperPlane /> Send</>}
                  </button>
                </form>
              )}
            </div>

            <hr />

            <div className="resolution-section mt-4 pt-2 bg-light p-4 rounded border">
              <h5 className="mb-3 d-flex align-items-center gap-2">
                <FaCheckCircle className="text-success" />
                Resolution & Finalize
              </h5>

              {ticket.status === 'resolved' || ticket.status === 'closed' ? (
                <div className="alert alert-success mt-3 mb-0 border-0 shadow-sm">
                  <h6 className="alert-heading fw-bold mb-2">Admin Resolution Notes (Closed on {new Date(ticket.resolvedAt).toLocaleDateString()})</h6>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{ticket.adminResponse}</p>
                </div>
              ) : (
                <form onSubmit={handleResolve} className="resolution-form">
                  <div className="mb-3">
                    <label className="form-label text-muted fw-bold">Internal / User Resolution Summary</label>
                    <textarea
                      className="form-control admin-textarea"
                      rows="3"
                      placeholder="Add final resolution notes to officially close this ticket..."
                      value={adminResponse}
                      onChange={(e) => setAdminResponse(e.target.value)}
                      required
                    ></textarea>
                    <small className="text-muted mt-1 d-block">This action will change the ticket status to 'RESOLVED' and close the conversation.</small>
                  </div>
                  <div className="text-end">
                    <button
                      type="submit"
                      className="btn btn-success px-4 d-flex align-items-center gap-2 ms-auto"
                      disabled={isResolving}
                    >
                      {isResolving ? (
                        <>Processing...</>
                      ) : (
                        <><FaCheckCircle /> Resolve Ticket</>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - User Info */}
        <div className="col-lg-4">
          <div className="content-card info-card user-profile-card sticky-top" style={{ top: '20px' }}>
            <div className="card-header border-bottom pb-3 mb-3 d-flex align-items-center gap-2">
              <FaUser className="text-primary" />
              <h5 className="mb-0">
                Filed By {isProvider ? 'Provider' : 'Customer'}
              </h5>
            </div>

            {user ? (
              <div className="profile-details">
                <div className="text-center mb-4">
                  <img
                    src={getFullUrl(user.avatar) || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.firstName || '')}+${encodeURIComponent(user.lastName || '')}&background=${isProvider ? '4834d4' : '50C8AC'}&color=fff&bold=true&size=200`}
                    alt="User Avatar"
                    className="rounded-circle shadow-sm mb-3"
                    style={{ width: '100px', height: '100px', objectFit: 'cover', border: '3px solid #fff', backgroundColor: '#f8fafc' }}
                    onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.firstName || 'U')}&background=${isProvider ? '4834d4' : '50C8AC'}&color=fff&bold=true&size=200`; 
                    }}
                  />

                  <h4 className="fw-bold mb-1">{user.firstName} {user.lastName}</h4>
                  <span className={`badge bg-${isProvider ? 'warning' : 'info'} text-dark`}>
                    {ticket.userRole.toUpperCase()}
                  </span>
                </div>

                <ul className="list-group list-group-flush mb-4">
                  <li className="list-group-item px-0 d-flex align-items-center gap-3">
                    <FaEnvelope className="text-muted" />
                    <span className="text-break">{user.email}</span>
                  </li>
                  {user.phone && (
                    <li className="list-group-item px-0 d-flex align-items-center gap-3">
                      <FaPhone className="text-muted" />
                      <span>{user.phone}</span>
                    </li>
                  )}
                  {user.zipCode && (
                    <li className="list-group-item px-0 d-flex align-items-center gap-3">
                      <FaMapMarkerAlt className="text-muted" />
                      <span>ZIP: {user.zipCode}</span>
                    </li>
                  )}
                  <li className="list-group-item px-0 d-flex align-items-center gap-3">
                    <FaCalendarAlt className="text-muted" />
                    <span>Joined: {new Date(user.createdAt).toLocaleDateString()}</span>
                  </li>
                </ul>

                <div className="text-center">
                  <Link
                    to={`/${isProvider ? 'providers' : 'customers'}/${user.id}`}
                    className="btn btn-outline-primary w-100"
                  >
                    View Full Profile
                  </Link>
                </div>
              </div>
            ) : (
              <p className="text-muted">User profile data not available.</p>
            )}
          </div>
        </div>
      </div>
      {/* Attachment Preview Modal */}
      {previewImage && (
        <div className="attachment-modal-overlay" onClick={() => setPreviewImage(null)}>
          <div className="attachment-modal-container" onClick={e => e.stopPropagation()}>
            <div className="attachment-modal-header">
              <div className="modal-info">
                <span className="modal-title-text">Attachment Preview</span>
              </div>
              <div className="modal-header-actions">
                <button
                  className="modal-download-btn"
                  onClick={() => handleDownload(previewImage)}
                >
                  <FaDownload /> Download
                </button>
                <button className="modal-close-btn" onClick={() => setPreviewImage(null)}>
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="attachment-modal-body">
              <img src={previewImage} alt="Attachment Preview" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetails;
